import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  Loader2, 
  AlertCircle,
  ArrowRight,
  Mail
} from 'lucide-react';

const SponsorshipSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [pollCount, setPollCount] = useState(0);

  const verifyPayment = useCallback(async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    
    if (attempt >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. If you were charged, please contact support.');
      return;
    }

    try {
      const res = await fetch(`${API}/sponsorship/verify-payment?session_id=${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.ok) {
        const data = await res.json();
        setStatus('success');
        setMessage(data.message || 'Payment verified successfully!');
        toast.success('Payment successful! Check your email for login credentials.');
      } else if (res.status === 400) {
        // Payment not yet complete, retry
        setPollCount(attempt + 1);
        setTimeout(() => verifyPayment(sessionId, attempt + 1), 2000);
      } else {
        const error = await res.json();
        setStatus('error');
        setMessage(error.detail || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setPollCount(attempt + 1);
      setTimeout(() => verifyPayment(sessionId, attempt + 1), 2000);
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid session. Please try again.');
      return;
    }

    verifyPayment(sessionId);
  }, [searchParams, verifyPayment]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-neutral-800 border-neutral-700">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-emerald-400 mx-auto mb-6 animate-spin" />
              <h2 className="text-xl font-semibold text-white mb-2">Verifying Payment</h2>
              <p className="text-neutral-400 mb-4">Please wait while we confirm your payment...</p>
              {pollCount > 0 && (
                <p className="text-sm text-neutral-500">Attempt {pollCount + 1} of 5</p>
              )}
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-emerald-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Payment Successful!</h2>
              <p className="text-neutral-400 mb-6">{message}</p>
              
              <div className="bg-neutral-700/50 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white">Check Your Email</p>
                    <p className="text-sm text-neutral-400">
                      We&apos;ve sent your login credentials to your email address. Use them to access your Sponsorship Dashboard.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#39FF14] text-black hover:bg-[#32e612] font-semibold"
                >
                  Login to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Link to="/">
                  <Button variant="outline" className="w-full border-neutral-600 text-neutral-300 hover:bg-neutral-700">
                    Return to Home
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-12 h-12 text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Verification Issue</h2>
              <p className="text-neutral-400 mb-6">{message}</p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/sponsorship')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  Try Again
                </Button>
                <Link to="/">
                  <Button variant="outline" className="w-full border-neutral-600 text-neutral-300 hover:bg-neutral-700">
                    Return to Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SponsorshipSuccess;
